import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Building2, Gavel, LayersIcon, Layers } from "lucide-react";

const profiles = [
  { value: "legislativo", label: "Legislativo Municipal", icon: Building2, hint: "Câmaras, mandatos, fiscalização" },
  { value: "licitacoes", label: "Licitações Públicas", icon: Gavel, hint: "Lei 14.133, ETP, TR, pregão" },
  { value: "ambos", label: "Ambos", icon: Layers, hint: "Acesso completo às duas áreas" },
];

// Categorias oficiais de Órgão público disponíveis no auto-cadastro do aluno.
// As três últimas exigem que o usuário especifique o nome no campo adicional.
const ORGAO_CATEGORIAS = [
  { value: "Prefeitura", label: "Prefeitura", requiresSpecific: false, specificPlaceholder: "" },
  { value: "Câmara Municipal", label: "Câmara Municipal", requiresSpecific: false, specificPlaceholder: "" },
  { value: "Órgão estadual", label: "Órgão estadual", requiresSpecific: true, specificPlaceholder: "Ex.: Assembleia Legislativa de SP" },
  { value: "Órgão federal", label: "Órgão federal", requiresSpecific: true, specificPlaceholder: "Ex.: Ministério da Justiça" },
  { value: "Outro", label: "Outro", requiresSpecific: true, specificPlaceholder: "Especifique o órgão" },
];

// Junta categoria + especificação em uma única string compatível com o backend (campo `orgao`, texto livre).
export function buildOrgaoString(categoria, especifico) {
  const cat = (categoria || "").trim();
  const spec = (especifico || "").trim();
  if (!cat) return "";
  const meta = ORGAO_CATEGORIAS.find(o => o.value === cat);
  if (meta?.requiresSpecific && spec) return `${cat} — ${spec}`;
  return cat;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: "", cpf: "", email: "", phone: "", cargo: "",
    orgao_categoria: "", orgao_especifico: "",
    cidade: "", estado: "",
    profile: "ambos", password: "", confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const orgaoMeta = ORGAO_CATEGORIAS.find(o => o.value === form.orgao_categoria);
  const requiresSpecific = !!orgaoMeta?.requiresSpecific;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Senhas não conferem");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Senha deve ter ao menos 6 caracteres");
      return;
    }
    // Se o aluno escolheu uma categoria que exige nome específico, ele precisa preencher.
    if (form.orgao_categoria && requiresSpecific && !form.orgao_especifico.trim()) {
      toast.error("Informe o nome do órgão público");
      setStep(2);
      return;
    }
    const orgaoFinal = buildOrgaoString(form.orgao_categoria, form.orgao_especifico);
    setLoading(true);
    try {
      const u = await register({
        full_name: form.full_name, cpf: form.cpf, email: form.email, phone: form.phone,
        cargo: form.cargo, orgao: orgaoFinal, cidade: form.cidade, estado: form.estado,
        profile: form.profile, password: form.password,
      });
      toast.success(`Cadastro realizado! Seu CEAP ID: ${u.ceap_id}`);
      navigate("/home", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no cadastro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-hero-radial bg-white">
      <div className="max-w-[480px] mx-auto px-5 pt-8 pb-16">
        <div className="flex justify-center mb-6"><Logo size={40} /></div>
        <Card className="rounded-2xl border-[hsl(var(--border))] shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-1">
              <h1 className="font-display text-2xl font-semibold text-[hsl(var(--primary))]">Criar conta</h1>
              <span className="text-xs text-muted-foreground">Passo {step}/3</span>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Faltam poucos passos para acessar a plataforma.</p>
            <div className="h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden mb-6">
              <div className="h-full bg-[hsl(var(--accent))] transition-all" style={{ width: `${(step/3)*100}%` }} />
            </div>
            <form onSubmit={submit} className="space-y-4">
              {step === 1 && (
                <>
                  <div className="space-y-1.5"><Label>Nome completo</Label>
                    <Input required value={form.full_name} onChange={set("full_name")} placeholder="Seu nome" data-testid="reg-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>CPF</Label>
                      <Input required value={form.cpf} onChange={set("cpf")} placeholder="000.000.000-00" data-testid="reg-cpf" /></div>
                    <div className="space-y-1.5"><Label>Telefone</Label>
                      <Input value={form.phone} onChange={set("phone")} placeholder="(00) 00000-0000" data-testid="reg-phone" /></div>
                  </div>
                  <div className="space-y-1.5"><Label>E-mail</Label>
                    <Input type="email" required value={form.email} onChange={set("email")} placeholder="voce@exemplo.com" data-testid="reg-email" /></div>
                  <Button type="button" className="w-full h-11" onClick={() => setStep(2)} data-testid="reg-next-1">Continuar</Button>
                </>
              )}
              {step === 2 && (
                <>
                  <div className="space-y-1.5"><Label>Cargo</Label>
                    <Input value={form.cargo} onChange={set("cargo")} placeholder="Ex.: Vereador, Pregoeiro, Servidor" data-testid="reg-cargo" /></div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-orgao-categoria">Órgão público</Label>
                    <Select
                      value={form.orgao_categoria}
                      onValueChange={(v) => setForm(f => ({ ...f, orgao_categoria: v, orgao_especifico: "" }))}
                    >
                      <SelectTrigger id="reg-orgao-categoria" data-testid="reg-orgao-categoria">
                        <SelectValue placeholder="Selecione o tipo de órgão" />
                      </SelectTrigger>
                      <SelectContent>
                        {ORGAO_CATEGORIAS.map(o => (
                          <SelectItem key={o.value} value={o.value} data-testid={`reg-orgao-opt-${o.value}`}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {requiresSpecific && (
                      <div className="pt-2 space-y-1.5">
                        <Label htmlFor="reg-orgao-especifico" className="text-xs text-muted-foreground">
                          Especifique o nome do órgão
                        </Label>
                        <Input
                          id="reg-orgao-especifico"
                          value={form.orgao_especifico}
                          onChange={set("orgao_especifico")}
                          placeholder={orgaoMeta?.specificPlaceholder || "Nome do órgão"}
                          data-testid="reg-orgao-especifico"
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Cidade</Label>
                      <Input value={form.cidade} onChange={set("cidade")} placeholder="Cidade" data-testid="reg-cidade" /></div>
                    <div className="space-y-1.5"><Label>Estado</Label>
                      <Input maxLength={2} value={form.estado} onChange={(e) => setForm(f=>({...f, estado: e.target.value.toUpperCase()}))} placeholder="UF" data-testid="reg-estado" /></div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>Voltar</Button>
                    <Button type="button" className="flex-1" onClick={() => setStep(3)} data-testid="reg-next-2">Continuar</Button>
                  </div>
                </>
              )}
              {step === 3 && (
                <>
                  <div>
                    <Label className="mb-2 block">Sua área de interesse</Label>
                    <div className="grid grid-cols-1 gap-2">
                      {profiles.map(p => {
                        const Icon = p.icon;
                        const active = form.profile === p.value;
                        return (
                          <button
                            key={p.value}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, profile: p.value }))}
                            className={"flex items-center gap-3 p-3 rounded-xl border text-left transition " + (active ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.07)]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]") }
                            data-testid={`register-profile-${p.value}`}
                          >
                            <div className={"w-9 h-9 rounded-lg flex items-center justify-center " + (active ? "bg-[hsl(var(--accent))] text-white" : "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]") }>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm">{p.label}</p>
                              <p className="text-xs text-muted-foreground">{p.hint}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Senha</Label>
                      <Input type="password" required value={form.password} onChange={set("password")} placeholder="Mínimo 6 caracteres" data-testid="reg-password" /></div>
                    <div className="space-y-1.5"><Label>Confirmar</Label>
                      <Input type="password" required value={form.confirm} onChange={set("confirm")} placeholder="Repita a senha" data-testid="reg-confirm" /></div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(2)}>Voltar</Button>
                    <Button type="submit" className="flex-1 h-11" disabled={loading} data-testid="reg-submit">
                      {loading ? "Criando…" : "Criar conta"}
                    </Button>
                  </div>
                </>
              )}
            </form>
            <p className="text-center text-sm text-muted-foreground mt-5">
              Já tem conta?{" "}<Link to="/login" className="text-[hsl(var(--accent))] font-medium hover:underline">Entrar</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
